import React from 'react';
import { Box, BoxProps, useBreakpointIndex } from '@apia/theme';
import { getVariant } from '@apia/theme';
import { KeyHandler } from './KeyHandler';
import { TableRenderer } from './renderers/TableRenderer';
import { AccordionRenderer } from './renderers/AccordionRenderer';
import { TUseDomProps } from './store/selection/types';
import { responsiveTableStore } from './store';
import { useResponsiveTableContext } from './ResponsiveTableContext';

type TResponsiveTable = BoxProps;

/**
 * Este objeto determina qué partes de la tabla deben
 * ser 'debugueadas'. Sobre todo para conocer el
 * impacto en el performance de la misma.
 */
export const rtDebug = {
  cell: false,
  context: false,
  filterCell: false,
  filters: {
    additional: false,
    grouped: false,
    inner: false,
    responsive: false,
    sort: false,
  },
  header: false,
  headerCell: false,
  index: false,
  keyHandler: false,
  row: false,
  tableRenderer: false,
};

export const domPropsSelector: (name: string) => TUseDomProps =
  (name: string) =>
  (global: ReturnType<typeof responsiveTableStore.getState>) =>
    global.responsiveTableSlice[name];

/**
 * El ResponsiveTable renderiza una tabla en base a la
 * información provista por ResponsiveTableContext, quien
 * debe ser ancestro de este elemento. La finalidad última
 * de este componente es lograr responsividad y performance.
 *
 * Las props pasadas sarán colocadas directamente en el
 * wrapper de la tabla. Con esta estructura se busca que
 * a la hora de dar estilos a este componente, se pueda pasar
 * un variant al Wrapper, sin afectar los estilos por defecto
 * de la tabla ni del acordión en modo responsivo. Esto no limita
 * en ningún concepto la posibilidad de cambiar el variant a
 * la tabla o al acordión, ya que eso puede hacerse desde un
 * variante personalizado con la estructura mostrada en el
 * ejemplo.
 *
 * Además, de esta manera es posible aplicar estilo a algunos
 * componentes nada más, sin la necesidad de cambiar el layout
 * que usa la tabla por defecto. Por ejemplo, con esta estructura
 * se puede utilizar un variant personalizado para definir
 * el ancho de las columnas de la tabla.
 *
 * @example
 * 'customVariant': {
 *  '.responsiveTable__accordion': {
 *    variant: 'layout.tables.otherAccordion'
 *  }
 *  '.responsiveTable__table': {
 *    variant: 'layout.tables.otherTable'
 *  }
 * }
 */
const NoMemoResponsiveTable = React.forwardRef<
  HTMLDivElement,
  TResponsiveTable
>(({ className, variant, ...props }, ref) => {
  const breakpoint = useBreakpointIndex({ defaultIndex: 3 });
  const { currentBreakPoint } = useResponsiveTableContext();

  const actualBreakpoint = currentBreakPoint ?? breakpoint;

  return (
    <Box
      {...props}
      {...getVariant('layout.common.tables.responsive')}
      className={`responsiveTable__wrapper ${className ?? ''}`}
      ref={ref}
    >
      {actualBreakpoint >= 3 ? (
        <KeyHandler>
          <TableRenderer variant={variant} />
        </KeyHandler>
      ) : (
        <AccordionRenderer />
      )}
    </Box>
  );
});

NoMemoResponsiveTable.displayName = 'ResponsiveTable';

export const ResponsiveTable = React.memo(NoMemoResponsiveTable);
